import { onMounted, onBeforeUnmount } from 'vue';
import { http } from '@/api/http';

// access token 15min 过期; 12min 续一次, 留足余量.
const REFRESH_INTERVAL_MS = 12 * 60 * 1000;

/**
 * 前台登录态保活。
 *
 * 前台复用 admin 登录 cookie (与 /api 同源, 浏览器自动带 httpOnly cookie) 来查看私密内容,
 * 但 access token 仅 15min 有效。若不续期, 看私密超过 15min 就会降级成匿名 (私密消失)。
 *
 * 这里在登录态下定期静默调用 /auth/refresh (refresh cookie 7 天有效) 续 access:
 * - 启动先试一次 refresh: 成功 = 已登录 → 启动定时续期; 失败 = 匿名 → 什么都不做。
 * - 匿名访客的 refresh 失败被静默吞掉, 不报错、不跳转、不影响公开内容浏览。
 * - logout / 改密码会 bump tokenVersion → refresh 被服务端拒绝 → 续期失败即自动停止 (会话终止)。
 *
 * 安全性: token 始终在 httpOnly + sameSite=strict cookie, JS 不接触明文; 不新增端点;
 * 撤销机制 (tokenVersion) 照常生效。等价于把 admin 早有的 refresh 机制搬到前台。
 */
export function useSessionKeepalive(): void {
  let timer: ReturnType<typeof setInterval> | null = null;

  async function tryRefresh(): Promise<boolean> {
    try {
      await http.post('/auth/refresh', null, { withCredentials: true });
      return true;
    } catch {
      return false; // 匿名 / refresh 过期 / 已 logout → 静默
    }
  }

  function stop(): void {
    if (timer !== null) {
      clearInterval(timer);
      timer = null;
    }
  }

  onMounted(async () => {
    const loggedIn = await tryRefresh();
    if (!loggedIn) return; // 匿名访客: 不启动保活
    timer = setInterval(() => {
      void tryRefresh().then((ok) => {
        if (!ok) stop(); // 会话已终止 (logout / refresh 过期) → 停止续期
      });
    }, REFRESH_INTERVAL_MS);
  });

  onBeforeUnmount(stop);
}
